import { Check, ChevronDown } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { BidStagePill } from "@/components/dashboard/BidStagePill"
import { useBidLifecycleActions } from "@/components/bid-lifecycle/useBidLifecycleActions"
import {
  BID_STAGES,
  LIFECYCLE_ACTION_LABELS,
  STAGE_LABELS,
  bidLifecycleOf,
  bidPositionLabel,
  lifecycleActionsFor,
} from "@/lib/bid-lifecycle"
import type { Bid } from "@/lib/schemas"
import { cn } from "@/lib/utils"

interface BidStageControlProps {
  bid: Bid
  /** Viewers see the pill only, with no menu. */
  readOnly: boolean
}

/**
 * The stage control in the bid header (plan §2.7): a value select reading
 * "Write ▾". It lists the five stages, then the lifecycle actions for where
 * the bid is. A submitted or closed bid reads "Submitted" or its outcome, and
 * its stages are disabled until it is reopened. Choosing a stage is the only
 * thing that moves it: opening a page never does.
 */
export function BidStageControl({ bid, readOnly }: BidStageControlProps) {
  const actions = useBidLifecycleActions(bid)
  const position = bidLifecycleOf(bid)

  if (readOnly) return <BidStagePill bid={bid} />

  const isActive = position.lifecycle === "active"
  const lifecycleActions = lifecycleActionsFor(position.lifecycle)

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="h-8 gap-1.5 px-3"
            disabled={actions.isBusy}
            aria-label={`Stage: ${bidPositionLabel(position, { short: false })}`}
            data-testid="bid-stage-control"
          >
            {bidPositionLabel(position)}
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          {BID_STAGES.map((stage) => {
            const isCurrent = isActive && position.stage === stage
            return (
              <DropdownMenuItem
                key={stage}
                disabled={!isActive}
                onSelect={() => actions.moveStage(stage)}
                className="gap-2"
              >
                <Check className={cn("h-4 w-4", isCurrent ? "opacity-100" : "opacity-0")} aria-hidden />
                <span className={cn(isCurrent && "font-medium")}>{STAGE_LABELS[stage]}</span>
              </DropdownMenuItem>
            )
          })}
          <DropdownMenuSeparator />
          {lifecycleActions.map((action) => (
            <DropdownMenuItem key={action} onSelect={() => actions.run(action)} className="pl-8">
              {LIFECYCLE_ACTION_LABELS[action]}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      {actions.confirmDialog}
    </>
  )
}
